// Technique Configuration - Maps techniques to components and prompts

export const TECHNIQUE_TYPES = {
  WRITING: 'writing',
  MEDITATION: 'meditation',
  AFFIRMATION: 'affirmation'
};

export const TECHNIQUES = {
  "369": {
    id: "369",
    name: "369 Method",
    icon: "✨",
    type: TECHNIQUE_TYPES.WRITING,
    component: 'journal',
    description: "Write your desire 3 times in morning, 6 times afternoon, 9 times before bed",
    instructions: "This powerful numerology-based technique harnesses the power of repetition and sacred numbers.",
    journalPrompts: [
      "🌅 Morning (3x): Write your desire exactly 3 times. Focus on clarity and intention.",
      "☀️ Afternoon (6x): Write your desire 6 times. Feel the energy building.",
      "🌙 Evening (9x): Write your desire 9 times before bed. Release it to the universe."
    ],
    timerRecommendation: 600, // 10 minutes
    bestFor: ["abundance", "specific goals", "clarity"]
  },

  "scripting": {
    id: "scripting",
    name: "Scripting",
    icon: "📝",
    type: TECHNIQUE_TYPES.WRITING,
    component: 'journal',
    description: "Write in present tense as if your desire has already manifested",
    instructions: "Write your story as if it's happening RIGHT NOW. Use sensory details, emotions, and present tense.",
    journalPrompts: [
      "I am so grateful now that... (Describe your manifestation in detail)",
      "It feels amazing to... (How does your body feel?)",
      "Every day I wake up and... (What's your new daily reality?)",
      "People notice that I... (How has your life visibly changed?)"
    ],
    timerRecommendation: 900, // 15 minutes
    bestFor: ["love", "lifestyle", "general manifestation"]
  },

  "watermethod": {
    id: "watermethod",
    name: "Water Method",
    icon: "💧",
    type: TECHNIQUE_TYPES.MEDITATION,
    component: 'meditation',
    description: "Write affirmation on glass of water, drink it mindfully with intention",
    instructions: "Water holds intention. This technique combines writing, meditation, and physical action.",
    meditationScript: [
      "Get a glass of fresh water and a small piece of paper.",
      "Write your affirmation or desire on the paper.",
      "Place the paper under or on the glass.",
      "Hold your hands around the glass and close your eyes.",
      "Visualize your desire flowing into the water as golden light.",
      "When ready, drink the water slowly and intentionally.",
      "Feel the manifestation entering every cell of your body."
    ],
    duration: 300, // 5 minutes
    bestFor: ["healing", "quick shifts", "gratitude"]
  },

  "pillow": {
    id: "pillow",
    name: "Pillow Method",
    icon: "🛏️",
    type: TECHNIQUE_TYPES.WRITING,
    component: 'journal',
    description: "Write desire on paper, place under pillow for subconscious programming",
    instructions: "Your subconscious mind is most receptive during sleep. This technique programs it while you rest.",
    journalPrompts: [
      "Write your desire as a clear, positive statement in present tense",
      "Keep it concise - one powerful sentence or affirmation",
      "Add emotional words: 'I am joyfully...', 'I am peacefully...'",
      "You'll place this under your pillow tonight and for 7-30 nights"
    ],
    timerRecommendation: 300, // 5 minutes
    bestFor: ["love", "peace", "dreams"]
  },

  "55x5": {
    id: "55x5",
    name: "55x5 Method",
    icon: "5️⃣",
    type: TECHNIQUE_TYPES.WRITING,
    component: 'journal',
    description: "Write your affirmation 55 times for 5 consecutive days",
    instructions: "Commitment and repetition create neural pathways. This method requires discipline but delivers results.",
    journalPrompts: [
      "📝 Day 1/5: Write your affirmation 55 times below. Stay focused and present.",
      "Copy this affirmation exactly 55 times: '[Your affirmation]'",
      "Feel the energy build with each repetition.",
      "Note: Continue this for 5 consecutive days without breaking the chain."
    ],
    timerRecommendation: 900, // 15 minutes
    bestFor: ["focus", "persistence", "big goals"]
  },

  "visualization": {
    id: "visualization",
    name: "Visualization Anchoring",
    icon: "🎯",
    type: TECHNIQUE_TYPES.MEDITATION,
    component: 'meditation',
    description: "Create a detailed mental movie, anchor it with a physical gesture",
    instructions: "Visualization becomes powerful when anchored to a physical trigger you can use anytime.",
    meditationScript: [
      "Find a comfortable seated position and close your eyes.",
      "Take 3 deep breaths to center yourself.",
      "Create a vivid mental movie of your desire fulfilled.",
      "Engage ALL senses: What do you see, hear, feel, smell, taste?",
      "Feel the emotions of already having this.",
      "Choose a physical gesture (touching thumb to finger, hand on heart, etc.)",
      "At the peak of feeling, make your gesture.",
      "Repeat this 3 times, strengthening the anchor.",
      "Use this gesture anytime to recall this powerful state."
    ],
    duration: 600, // 10 minutes
    bestFor: ["confidence", "performance", "achievement"]
  },

  "emotional_ladder": {
    id: "emotional_ladder",
    name: "Emotional Ladder",
    icon: "🪜",
    type: TECHNIQUE_TYPES.WRITING,
    component: 'journal',
    description: "Move up emotional scale gradually - climb step by step",
    instructions: "You can't jump from depression to joy. But you CAN climb one step at a time.",
    journalPrompts: [
      "Where am I emotionally right now? (Name the feeling honestly)",
      "What would feel just slightly better? (Not perfect, just one step up)",
      "Write thoughts that support this slightly better feeling",
      "What small action could I take today that matches this better feeling?",
      "Emotional Scale: Depression → Anger → Frustration → Hope → Optimism → Joy"
    ],
    timerRecommendation: 600, // 10 minutes
    bestFor: ["healing", "depression", "anxiety"]
  },

  "two_cup": {
    id: "two_cup",
    name: "Two Cup Method",
    icon: "🥤",
    type: TECHNIQUE_TYPES.MEDITATION,
    component: 'meditation',
    description: "Label cups 'current' and 'desired', pour water between them, drink",
    instructions: "This quantum jumping technique uses water as a carrier of intention between realities.",
    meditationScript: [
      "Get 2 cups and 2 small pieces of paper.",
      "Label one: 'Current Reality' (describe where you are now)",
      "Label the other: 'Desired Reality' (describe where you want to be)",
      "Place labels on the cups.",
      "Fill the 'Current Reality' cup with water.",
      "Hold the current cup, acknowledge where you are with gratitude.",
      "Slowly pour the water into the 'Desired Reality' cup.",
      "As you pour, visualize jumping timelines to your desired reality.",
      "Drink from the 'Desired Reality' cup slowly and intentionally.",
      "Feel yourself in the new reality NOW."
    ],
    duration: 300, // 5 minutes
    bestFor: ["transformation", "career", "life changes"]
  },

  "gratitude_rampage": {
    id: "gratitude_rampage",
    name: "Gratitude Rampage",
    icon: "🙏",
    type: TECHNIQUE_TYPES.AFFIRMATION,
    component: 'affirmations',
    description: "List everything you're grateful for as fast as you can for 5 minutes",
    instructions: "Speed and momentum raise your vibration instantly. Don't overthink - just write!",
    journalPrompts: [
      "Set a 5-minute timer and write FAST - no editing!",
      "I'm grateful for... (Start listing everything, big and small)",
      "Keep going even when you think you're done",
      "The small stuff counts: hot coffee, clean water, a text from a friend",
      "Feel your energy shift as you write"
    ],
    timerRecommendation: 300, // 5 minutes
    bestFor: ["gratitude", "raising vibration", "quick mood shift"],
    affirmationCount: 20 // Rapid-fire gratitude statements
  }
};

// Helper: Get technique by ID
export const getTechnique = (techniqueId) => {
  return TECHNIQUES[techniqueId] || null;
};

// Helper: Get all techniques of a certain type
export const getTechniquesByType = (type) => {
  return Object.values(TECHNIQUES).filter(t => t.type === type);
};

// Helper: Determine which component to route to
export const getComponentForTechnique = (techniqueId) => {
  const technique = getTechnique(techniqueId);
  if (!technique) return 'journal'; // Default fallback
  
  return technique.component;
};

// Helper: Get prompts for technique
export const getPromptsForTechnique = (techniqueId) => {
  const technique = getTechnique(techniqueId);
  return technique?.journalPrompts || [];
};

// Helper: Get meditation script for technique
export const getMeditationScriptForTechnique = (techniqueId) => {
  const technique = getTechnique(techniqueId);
  return technique?.meditationScript || [];
};